import React, { useCallback, useEffect, useMemo, useState } from "react";
import { listSecureFiles } from "../../../../services/secureFiles";
import { formatSecureFileLinks } from "../../../../utils/insertTextAtSelection";
import { filterSecureFilesByQuery } from "../../../../utils/secureFileSearch";
import { formatUploadedAt } from "../../../../utils/secureFileList";
import "./styles.css";

const mapSelectableFile = (file) => ({
  ...file,
  key: file.shareToken,
  uploadedAtLabel: formatUploadedAt(file.createdAt),
});

const SecureFileLinkPicker = ({ isOpen, onClose, clientAppointmentId, onInsertLinks }) => {
  const [allFiles, setAllFiles] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const resetPickerState = useCallback(() => {
    setSearchInput("");
    setStagedFiles([]);
    setError("");
  }, []);

  const loadFiles = useCallback(async () => {
    if (!clientAppointmentId) {
      return;
    }

    setIsLoading(true);
    setError("");

    const patientResult = await listSecureFiles(clientAppointmentId);

    if (patientResult.error) {
      setError(patientResult.error.message || "Failed to load secure files.");
      setAllFiles([]);
    } else {
      setAllFiles(
        (patientResult.data || [])
          .filter((file) => file.shareActive && file.clientAppointmentId === clientAppointmentId)
          .map(mapSelectableFile)
      );
    }

    setIsLoading(false);
  }, [clientAppointmentId]);

  useEffect(() => {
    if (isOpen) {
      resetPickerState();
      loadFiles();
    }
  }, [isOpen, loadFiles, resetPickerState]);

  const searchResults = useMemo(() => filterSecureFilesByQuery(allFiles, searchInput), [allFiles, searchInput]);

  const stagedKeys = useMemo(() => new Set(stagedFiles.map((file) => file.key)), [stagedFiles]);

  const addToStaged = (file) => {
    setStagedFiles((current) => (current.some((item) => item.key === file.key) ? current : [...current, file]));
  };

  const removeFromStaged = (fileKey) => {
    setStagedFiles((current) => current.filter((file) => file.key !== fileKey));
  };

  const toggleSelection = (file) => {
    if (stagedKeys.has(file.key)) {
      removeFromStaged(file.key);
      return;
    }
    addToStaged(file);
  };

  const handleInsert = () => {
    if (!stagedFiles.length) {
      return;
    }

    onInsertLinks(formatSecureFileLinks(stagedFiles));
    onClose();
  };

  const renderFileItem = (file) => (
    <label key={file.key} className="secure-file-link-picker-item is-size-7">
      <input type="checkbox" checked={stagedKeys.has(file.key)} onChange={() => toggleSelection(file)} />
      <span className="secure-file-link-picker-item-label">
        <span className="secure-file-link-picker-item-name has-text-weight-semibold">{file.fileName}</span>
        <span className="is-block has-text-grey">
          {file.uploadedByName || "Unknown"} · {file.uploadedAtLabel}
        </span>
      </span>
    </label>
  );

  const renderStagedFiles = () => {
    if (!stagedFiles.length) {
      return null;
    }

    return (
      <div className="secure-file-link-picker-section secure-file-link-picker-selected">
        <p className="is-size-7 has-text-weight-semibold mb-2">Selected files ({stagedFiles.length})</p>
        <div className="secure-file-link-picker-list">
          {stagedFiles.map((file) => (
            <div key={file.key} className="secure-file-link-picker-selected-item is-size-7">
              <span className="secure-file-link-picker-item-name has-text-weight-semibold">{file.fileName}</span>
              <button
                type="button"
                className="button is-small is-light is-radiusless"
                onClick={() => removeFromStaged(file.key)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (isLoading) {
      return <p className="has-text-grey is-size-7">Loading secure files...</p>;
    }

    if (!allFiles.length) {
      return (
        <p className="secure-file-link-picker-empty has-text-grey is-size-7">
          No active secure files have been uploaded for this patient. Upload one in the Secure File tab first.
        </p>
      );
    }

    if (!searchResults.length) {
      return <p className="secure-file-link-picker-empty has-text-grey is-size-7">No files match your search.</p>;
    }

    return <div className="secure-file-link-picker-list">{searchResults.map(renderFileItem)}</div>;
  };

  return (
    <div className={`modal secure-file-link-picker ${isOpen ? "is-active" : ""}`}>
      <div className="modal-background" onClick={onClose} />
      <div className="modal-content">
        <div className="box">
          <h3 className="title is-5">Insert Secure File Link</h3>
          <p className="is-size-7 has-text-grey mb-4">
            Only files uploaded to this patient's Secure File tab can be inserted. Select the files you want, then
            insert the links into the email.
          </p>

          <div className="field secure-file-link-picker-search">
            <label className="label is-size-7" htmlFor="secure-file-link-search">
              Search this patient's secure files
            </label>
            <div className="control">
              <input
                id="secure-file-link-search"
                className="input is-small is-radiusless"
                type="search"
                placeholder="Filter by file name or uploader..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {error && <div className="notification is-danger is-light is-size-7 py-2 px-3 mb-4">{error}</div>}

          {renderStagedFiles()}
          {renderResults()}

          <div className="field is-grouped is-grouped-right mt-5">
            <div className="control">
              <button type="button" className="button is-small is-light is-radiusless" onClick={onClose}>
                Cancel
              </button>
            </div>
            <div className="control">
              <button
                type="button"
                className="button is-small is-info is-radiusless"
                onClick={handleInsert}
                disabled={isLoading || !stagedFiles.length}
              >
                Insert Selected Links
              </button>
            </div>
          </div>
        </div>
      </div>
      <button className="modal-close is-large" aria-label="close" onClick={onClose} />
    </div>
  );
};

export default SecureFileLinkPicker;
